import { ElementType, useContext, useState } from "react";
import {
    Box,
    IconButton,
    useTheme,
    Stack,
    Tooltip
} from "@mui/material";
import { UsersContext, UserView } from "@my-helpers/common";
import { useNavigate } from "react-router-dom";
import { PeopleAlt, Add } from "@mui/icons-material";
import { ArrowBack } from "@mui/icons-material";
import Search from '@/components/Search';
import ListTitle from '@/components/ListTitle';
import { DeleteUser, UsersList } from "@/components/user";
import { Notification } from "@/components/Notification";

interface UsersViewProps {
    userId?: string;
}

const UsersView: ElementType<UsersViewProps> = ({ userId }) => {
    const context = useContext(UsersContext);
    if (context === undefined) {
        throw new Error('useUsersContext must be used within a UsersProvider');
    }
    const { isLoading, error, users } = context.view;
    const { deleteUser, updateUser, clearError } = context.command;
    const theme = useTheme();
    const navigate = useNavigate();

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<UserView | null>(null);

    const handleBackNavigation = () => {
        // Check if there's a previous page in history
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            // Fallback to home if no previous page
            navigate('/');
        }
    };

    if (userId) return;

    return (
        <Stack padding={theme.spacing(3)}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 3
            }}>
                <ListTitle title="Users" icon={PeopleAlt} />
                <Search placeholder="Search users..." />
                <Box sx={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1
                }}>
                    <Tooltip title="Add new user" arrow>
                        <IconButton
                            color="primary"
                            onClick={() => console.log('Add user clicked')}
                            sx={{
                                backgroundColor: 'primary.main',
                                color: 'white',
                                flexShrink: 0,
                                '&:hover': {
                                    backgroundColor: 'primary.dark',
                                }
                            }}
                        >
                            <Add />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Back">
                        <IconButton
                            onClick={handleBackNavigation}
                            sx={{
                                color: 'primary.light',
                                backgroundColor: 'lightGray',
                                flexShrink: 0,
                                '&:hover': {
                                    color: 'white',
                                    backgroundColor: 'primary.light',
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <UsersList
                users={users}
                updateUser={updateUser}
                setUserToDelete={setUserToDelete}
                setDeleteDialogOpen={setDeleteDialogOpen}
            />
            <DeleteUser
                userToDelete={userToDelete}
                deleteDialogOpen={deleteDialogOpen}
                deleteUser={deleteUser}
                setDeleteDialogOpen={setDeleteDialogOpen}
                setUserToDelete={setUserToDelete}
            />
            <Notification
                isLoading={isLoading}
                clearError={clearError}
                error={error}
                delay={300}
                autoHideDuration={1200}
            />
        </Stack>

    );
};

export default UsersView;
